import {useEffect} from 'react'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Helmet} from 'react-helmet-async'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import {Button} from '@/components/ui/button'
import {captureNotice} from '@/lib/sentry'
import {isLanguagePageSlug, languagePagesFor} from '@/lib/languagePages'
import {languageDisplayName} from '@/lib/languageNames'
import {isSupportedLocale} from '@/lib/locales'
import type {LanguagePageVariant} from '@/types/languagePage'

interface Props {
  variant: LanguagePageVariant
  slug: string
  locale?: string
}

// Keys are resolved at render with t(). A would-be tutor lands on sign-up with
// the tutor role already picked; a would-be student on the tutor search.
const COPY = {
  teach: {
    titleKey: 'nav.links.becomeTutor',
    headingKey: 'languagePageMissing.teach.heading',
    bodyKey: 'languagePageMissing.teach.body',
    ctaKey: 'languagePageMissing.teach.cta',
    ctaHref: '/signup?role=tutor',
    pagePath: '/become-a-tutor',
  },
  learn: {
    titleKey: 'nav.links.findTutor',
    headingKey: 'languagePageMissing.learn.heading',
    bodyKey: 'languagePageMissing.learn.body',
    ctaKey: 'languagePageMissing.learn.cta',
    ctaHref: '/student/find-tutors',
    pagePath: '/find-a-tutor',
  },
} as const

// A /become-a-tutor/:language or /find-a-tutor/:language address Sanity has no
// page for. Jynio accepts tutors of every teachable language, not just the few
// with a page, so this is a way in rather than a "page not found".
const LanguagePageMissing = ({variant, slug, locale}: Props) => {
  const {t, i18n} = useTranslation()
  const copy = COPY[variant]
  // Keep a visitor on a /fr/ page in French: an unprefixed link is English.
  const localePrefix = locale && locale !== 'en' && isSupportedLocale(locale) ? `/${locale}` : ''
  const otherPages = languagePagesFor(variant).filter((page) => page.slug !== slug)

  useEffect(() => {
    if (isLanguagePageSlug(slug, variant)) {
      // The Header and Footer link here, so the Sanity doc was unpublished,
      // deleted or renamed: a page of ours is broken.
      captureNotice('Language page missing from Sanity', {level: 'warning', tags: {slug, variant}})
    } else {
      // Nothing links here; the visitor guessed the address. Which languages
      // they try is a demand signal, not a fault.
      captureNotice('Language page not offered', {level: 'info', tags: {slug, variant}})
    }
  }, [slug, variant])

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{`${t(copy.titleKey)} | Jynio`}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Header />
      <main className="container mx-auto px-4 py-32 max-w-xl text-center">
        <h1 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-3 tracking-tight">
          {t(copy.headingKey)}
        </h1>
        <p className="text-base md:text-lg text-muted-foreground mb-8 leading-relaxed">
          {t(copy.bodyKey)}
        </p>
        <Button asChild size="lg">
          <Link to={copy.ctaHref}>{t(copy.ctaKey)}</Link>
        </Button>
        {otherPages.length > 0 ? (
          <section className="mt-14">
            <h2 className="text-sm font-semibold text-foreground mb-3">
              {t('languagePageMissing.otherPages')}
            </h2>
            <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2">
              {otherPages.map((page) => (
                <li key={page.slug}>
                  <Link
                    to={`${localePrefix}${copy.pagePath}/${page.slug}`}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors rounded-sm focus-ring"
                  >
                    {languageDisplayName(page.code, page.name, i18n.language)}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </main>
      <Footer />
    </div>
  )
}

export default LanguagePageMissing
